import { localBackend } from "@/integrations/local/client";
import { articles as staticArticles, type Article, type ArticleCategory } from "@/data/articles";

export type { Article, ArticleCategory };

interface DbArticleRow {
  slug: string;
  title: string;
  excerpt: string | null;
  body_html: string;
  hero_url: string | null;
  category: ArticleCategory;
  published_at: string | null;
  meta_description: string | null;
}

/** Merge: DB published articles override static by slug; new DB-only slugs are added. */
export async function getMergedArticles(category?: ArticleCategory): Promise<Article[]> {
  const { data } = await localBackend
    .from("articles")
    .select("slug,title,excerpt,body_html,hero_url,category,published_at,meta_description")
    .eq("status", "published");

  const dbBySlug = new Map<string, Article>();
  ((data ?? []) as DbArticleRow[]).forEach((r) => {
    dbBySlug.set(r.slug, {
      slug: r.slug,
      category: r.category,
      title: r.title,
      excerpt: r.excerpt ?? "",
      metaDescription: r.meta_description ?? r.excerpt ?? "",
      date: (r.published_at ?? new Date().toISOString()).slice(0, 10),
      hero: r.hero_url ?? "",
      bodyHtml: r.body_html,
    });
  });

  const merged: Article[] = [];
  staticArticles.forEach((a) => merged.push(dbBySlug.get(a.slug) ?? a));
  dbBySlug.forEach((a, slug) => {
    if (!staticArticles.some((s) => s.slug === slug)) merged.push(a);
  });

  const filtered = category ? merged.filter((a) => a.category === category) : merged;
  return filtered.sort((a, b) => (a.date < b.date ? 1 : -1));
}

export async function getMergedArticleBySlug(slug: string): Promise<Article | undefined> {
  const all = await getMergedArticles();
  return all.find((a) => a.slug === slug);
}
